import React, { useEffect, useRef, useState } from "react";

// How far down the viewport the "you are here" line sits. Below the sticky
// header, so a heading counts as current once it has settled under it rather
// than the instant it appears at the bottom of the screen.
const ACTIVE_LINE_OFFSET = 140;

// Posts run to fifteen h2 sections over ten thousand characters, with no way
// to see the shape of one or jump within it. Built from the same parse that
// gives the headings their ids, so the list and the anchors cannot drift.
//
// Rendered twice rather than toggled: a <details> that must start open on
// desktop and closed on mobile cannot be expressed in CSS alone, and the
// alternative -- JavaScript reading a media query -- would leave the
// prerendered snapshot in whichever state the crawler happened to be in.
// The markup is a short list of links; duplicating it costs almost nothing.
const BlogToc = ({ headings }) => {
  const [activeId, setActiveId] = useState(null);
  const navRef = useRef(null);

  // Two or three sections do not need navigating. Computed rather than
  // early-returned because the effect below cannot be conditional.
  const enabled = Boolean(headings && headings.length >= 4);

  useEffect(() => {
    if (!enabled) return undefined;

    // The headings live inside the article's dangerouslySetInnerHTML, so they
    // are reachable by id but not by ref.
    const sections = headings
      .map((h) => document.getElementById(h.id))
      .filter(Boolean);
    if (!sections.length) return undefined;

    let frame = 0;

    const update = () => {
      frame = 0;
      const line = window.scrollY + ACTIVE_LINE_OFFSET;

      // The last heading that has passed the line, rather than the first one
      // intersecting it: between two headings nothing is on screen to
      // intersect, and an IntersectionObserver would leave the list blank
      // through the body of a long section.
      let current = sections[0].id;
      for (const el of sections) {
        if (el.getBoundingClientRect().top + window.scrollY <= line) {
          current = el.id;
        } else break;
      }

      // The final section is often shorter than the gap between the line and
      // the bottom of the page, so it could never become current on its own.
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 80;
      if (atBottom) current = sections[sections.length - 1].id;

      setActiveId((prev) => (prev === current ? prev : current));
    };

    // Coalesced to one read per frame: this measures every heading, and
    // scroll fires far more often than the screen repaints.
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled, headings]);

  // On a fifteen-section post the list scrolls inside its own box, so the
  // current item can sit outside it. Only the nav's own scrollTop is touched
  // -- scrollIntoView would drag the page with it.
  useEffect(() => {
    if (!activeId || !navRef.current) return;
    const nav = navRef.current;
    // No escaping needed: the value sits inside a quoted attribute selector
    // and slugify only ever emits [a-z0-9-].
    const link = nav.querySelector(`a[href="#${activeId}"]`);
    if (!link) return;
    const navBox = nav.getBoundingClientRect();
    const linkBox = link.getBoundingClientRect();
    if (linkBox.top < navBox.top) {
      nav.scrollTop -= navBox.top - linkBox.top + 12;
    } else if (linkBox.bottom > navBox.bottom) {
      nav.scrollTop += linkBox.bottom - navBox.bottom + 12;
    }
  }, [activeId]);

  if (!enabled) return null;

  // Deliberately unnumbered. Several posts are listicles whose headings
  // already start "1.", "2." -- an index column next to those renders as
  // "01  1. Dust and Dirt on Solar Panels". A left rule marks the items
  // instead and works whatever the heading text is.
  const list = (
    <ul className="space-y-1">
      {headings.map((h) => {
        const isActive = h.id === activeId;
        return (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              // "location" rather than "true": this marks position within a
              // page, not the current page in a set of links.
              aria-current={isActive ? "location" : undefined}
              className={`block border-l-2 py-1 pl-3 text-[14px] leading-snug transition-colors ${
                isActive
                  ? "border-[#117816] font-semibold text-[#117816]"
                  : "border-[#e8efe8] text-[#4b5563] hover:border-[#117816] hover:text-[#117816]"
              }`}
            >
              {h.text}
            </a>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {/* Mobile: collapsed by default so it never pushes the article down. */}
      <details className="lg:hidden mb-6 rounded-2xl border border-[#edf1ed] bg-white p-5 shadow-sm">
        <summary className="cursor-pointer list-none font-semibold text-[#111827] [&::-webkit-details-marker]:hidden">
          On this page
          <span className="ml-2 text-[13px] font-normal text-[#6b7280]">
            ({headings.length} sections)
          </span>
        </summary>
        <nav aria-label="Table of contents" className="mt-4">
          {list}
        </nav>
      </details>

      {/* Desktop: sticky alongside the article.
          flex-1 is what makes the sticking work. The sticky nav can only
          travel within its own parent, and an aside sized to its content is
          590px tall -- so the contents list unpinned a few hundred pixels in.
          Growing the aside to fill the column hands the nav the whole article
          to stick through, and it stops short of the cards pinned to the
          bottom, so those are revealed rather than overlapped as the FAQ
          arrives. aria-hidden on the mobile copy is not needed -- only one is
          in the layout at any width. */}
      <aside className="hidden lg:block lg:flex-1 lg:min-h-0">
        <nav
          ref={navRef}
          aria-label="Table of contents"
          className="sticky top-24 rounded-2xl border border-[#edf1ed] bg-white p-6 shadow-sm max-h-[calc(100vh-8rem)] overflow-y-auto"
        >
          <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#117816]">
            On this page
          </p>
          {list}
        </nav>
      </aside>
    </>
  );
};

export default BlogToc;
